/**
 * Secure Storage Utilities
 * Provides encrypted localStorage wrapper using Web Crypto API
 * 
 * Security Features:
 * - AES-GCM encryption (authenticated encryption)
 * - Cryptographically secure random keys
 * - Automatic data validation and versioning
 * - Protection against tampering
 */

const STORAGE_VERSION = 1;
const ALGORITHM = 'AES-GCM';
const KEY_LENGTH = 256;

// Generate a cryptographically secure encryption key
const generateKey = async (): Promise<CryptoKey> => {
  return await crypto.subtle.generateKey(
    {
      name: ALGORITHM,
      length: KEY_LENGTH,
    },
    true,
    ['encrypt', 'decrypt']
  );
};

// Store the key securely in sessionStorage (base64 encoded)
const getOrCreateKey = async (): Promise<CryptoKey> => {
  try {
    const storedKey = sessionStorage.getItem('_crypto_key');
    
    if (storedKey) {
      // Import existing key
      const keyData = Uint8Array.from(atob(storedKey), c => c.charCodeAt(0));
      return await crypto.subtle.importKey(
        'raw',
        keyData,
        { name: ALGORITHM, length: KEY_LENGTH },
        true,
        ['encrypt', 'decrypt']
      );
    }
    
    // Generate new key
    const key = await generateKey();
    const exported = await crypto.subtle.exportKey('raw', key);
    const keyArray = new Uint8Array(exported);
    const base64Key = btoa(String.fromCharCode(...keyArray));
    sessionStorage.setItem('_crypto_key', base64Key);
    
    return key;
  } catch (error) {
    console.error('[SecureStorage] Key generation error:', error);
    throw new Error('Failed to initialize encryption');
  }
};

// Encrypt data using AES-GCM
const encryptData = async (data: string): Promise<string> => {
  try {
    const key = await getOrCreateKey();
    const iv = crypto.getRandomValues(new Uint8Array(12)); // 96-bit IV for GCM
    const encoder = new TextEncoder();
    const encodedData = encoder.encode(data);
    
    const encryptedData = await crypto.subtle.encrypt(
      {
        name: ALGORITHM,
        iv: iv,
      },
      key,
      encodedData
    );
    
    // Combine IV + encrypted data
    const combined = new Uint8Array(iv.length + encryptedData.byteLength);
    combined.set(iv, 0);
    combined.set(new Uint8Array(encryptedData), iv.length);
    
    return btoa(String.fromCharCode(...combined));
  } catch (error) {
    console.error('[SecureStorage] Encryption error:', error);
    throw new Error('Encryption failed');
  }
};

// Decrypt data using AES-GCM
const decryptData = async (encryptedData: string): Promise<string> => {
  try {
    const key = await getOrCreateKey();
    const combined = Uint8Array.from(atob(encryptedData), c => c.charCodeAt(0));
    
    const iv = combined.slice(0, 12);
    const data = combined.slice(12);
    
    const decryptedData = await crypto.subtle.decrypt(
      {
        name: ALGORITHM,
        iv: iv,
      },
      key,
      data
    );
    
    const decoder = new TextDecoder();
    return decoder.decode(decryptedData);
  } catch (error) {
    console.error('[SecureStorage] Decryption error:', error);
    throw new Error('Decryption failed');
  }
};

/**
 * Wrapper for encrypted data with version and integrity check
 */
interface StorageWrapper {
  version: number;
  data: string;
  timestamp: number;
}

/**
 * Secure localStorage wrapper with encryption
 * Note: For non-sensitive data (like UI preferences), use getItemSync/setItemSync
 * For sensitive data, always use the async versions
 */
export const secureStorage = {
  /**
   * Set item with encryption (async)
   * Use for sensitive data that requires encryption
   */
  setItem: async (key: string, value: any): Promise<void> => {
    try {
      const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
      const encrypted = await encryptData(stringValue);
      
      const wrapper: StorageWrapper = {
        version: STORAGE_VERSION,
        data: encrypted,
        timestamp: Date.now(),
      };
      
      localStorage.setItem(key, JSON.stringify(wrapper));
    } catch (error) {
      console.error('[SecureStorage] Error setting item:', error);
      throw error;
    }
  },

  /**
   * Get item with decryption and validation (async)
   * Use for sensitive data that requires decryption
   */
  getItem: async <T = string>(key: string): Promise<T | null> => {
    try {
      const stored = localStorage.getItem(key);
      if (!stored) return null;

      // Try to parse as JSON first
      let wrapper: StorageWrapper;
      try {
        wrapper = JSON.parse(stored);
      } catch {
        // Not valid JSON - this is legacy data from another library, ignore silently
        return null;
      }
      
      // Check if it's a valid StorageWrapper structure (our format)
      if (typeof wrapper !== 'object' || wrapper === null || 
          !('version' in wrapper) || !('data' in wrapper) || !('timestamp' in wrapper)) {
        // Not our format - data from another library, ignore silently
        return null;
      }
      
      // Validate version
      if (wrapper.version !== STORAGE_VERSION) {
        console.warn('[SecureStorage] Version mismatch, removing stale data');
        localStorage.removeItem(key);
        return null;
      }
      
      const decrypted = await decryptData(wrapper.data);
      
      // Try to parse as JSON, fallback to string
      try {
        return JSON.parse(decrypted) as T;
      } catch {
        return decrypted as T;
      }
    } catch (error) {
      console.error('[SecureStorage] Error getting item:', error);
      // Remove corrupted data
      localStorage.removeItem(key);
      return null;
    }
  },
  
  /**
   * Set item without encryption (sync)
   * Use ONLY for non-sensitive data like UI preferences, active profile ID, etc.
   * NEVER use for passwords, tokens, or personal information
   */
  setItemSync: (key: string, value: any): void => {
    try {
      const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
      localStorage.setItem(key, stringValue);
    } catch (error) {
      console.error('[SecureStorage] Error setting item (sync):', error);
    }
  },
  
  /**
   * Get item without decryption (sync)
   * Use ONLY for non-sensitive data
   */
  getItemSync: <T = string>(key: string): T | null => {
    try {
      const stored = localStorage.getItem(key);
      if (!stored) return null;
      
      // Try to parse as JSON, fallback to string
      try {
        return JSON.parse(stored) as T;
      } catch {
        return stored as T;
      }
    } catch (error) {
      console.error('[SecureStorage] Error getting item (sync):', error);
      return null;
    }
  },

  /**
   * Remove item
   */
  removeItem: (key: string): void => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('[SecureStorage] Error removing item:', error);
    }
  },

  /**
   * Clear all items
   */
  clear: (): void => {
    try {
      localStorage.clear();
      sessionStorage.removeItem('_crypto_key');
    } catch (error) {
      console.error('[SecureStorage] Error clearing storage:', error);
    }
  },

  /**
   * Check if item exists
   */
  hasItem: (key: string): boolean => {
    return localStorage.getItem(key) !== null;
  },
};

/**
 * Secure session storage with auto-expiration
 */
export const secureSession = {
  /**
   * Set item with expiration (in milliseconds)
   */
  setItem: async (key: string, value: any, expiresIn: number = 3600000): Promise<void> => {
    const item = {
      value,
      expires: Date.now() + expiresIn,
    };
    await secureStorage.setItem(key, item);
  },

  /**
   * Get item if not expired
   */
  getItem: async <T = any>(key: string): Promise<T | null> => {
    const item = await secureStorage.getItem<{ value: T; expires: number }>(key);
    
    if (!item) return null;
    
    // Check expiration
    if (Date.now() > item.expires) {
      secureStorage.removeItem(key);
      return null;
    }
    
    return item.value;
  },

  /**
   * Remove item
   */
  removeItem: (key: string): void => {
    secureStorage.removeItem(key);
  },
};

/**
 * Clean up expired session items on page load
 */
export const cleanupExpiredSessions = async (): Promise<void> => {
  try {
    const keys = Object.keys(localStorage);
    for (const key of keys) {
      try {
        const item = await secureStorage.getItem<{ expires?: number }>(key);
        if (item?.expires && Date.now() > item.expires) {
          secureStorage.removeItem(key);
        }
      } catch {
        // Skip invalid items
        continue;
      }
    }
  } catch (error) {
    console.error('[SecureStorage] Error cleaning up sessions:', error);
  }
};
